<template>
  <base-panel full-width no-margin no-padding>
    <template v-if="type === DISCUSSION_TYPE_GUIDES" #header>
      <panel-header
        low-weight
        :text="$t('discussion')"
        :anchor-id="DISCUSSION_ID"
        :panel-loading="isLoading"
      >
        <template #tip>
          <div class="tw-w-full tw-flex tw-items-center tw-mb-1">
            <span
              class="tw-flex tw-size-1 tw-rounded-full tw-bg-[var(--tile-text)] tw-opacity-40 tw-mx-2"
            />
            <span class="tw-text-md sm:tw-text-lg tw-text-primary">
              {{ discussionText }}
            </span>
          </div>
        </template>
      </panel-header>
    </template>
    <discussion-form
      v-if="canComment"
      :type="type"
      :resource="resource"
      :panel-loading="isLoading"
      :game-name-id="gameNameId"
    />

    <div v-if="discussions?.length" class="tw-space-y-6">
      <discussion-card
        v-for="discussion in discussions"
        :key="discussion.id"
        :type="type"
        :game-name-id="gameNameId"
        :resource-name-id="resource.name_id"
        :comment="discussion"
      >
        <template #default="{ replyId, commentText, toggle }">
          <discussion-form
            :game-name-id="gameNameId"
            :type="type"
            :resource="resource"
            :reply-id="replyId"
            :comment-text="commentText"
            :submit-text="isNumber(editId) ? 'save' : 'reply'"
            @toggle="toggle()"
          />
        </template>
      </discussion-card>
    </div>

    <div
      v-if="moreCommentsToLoad"
      class="tw-w-full tw-mt-8 tw-flex tw-justify-center"
    >
      <base-button auto-width :status="status" @click="loadMoreComments">{{
        $t('load_more_comments')
      }}</base-button>
    </div>
  </base-panel>
</template>

<script>
import { DISCUSSION_TYPE_GUIDES, DISCUSSION_TYPES } from '@config/options.js'
import DiscussionForm from '@shared/components/Discussion/DiscussionForm.vue'
import DiscussionCard from '@shared/components/Discussion/DiscussionCard.vue'
import { useStatus, useAsync, addToast, useRoute } from '@composables'
import { scrollToElement, isNumber } from '@helpers/utils.js'
import { DISCUSSION_ID } from '@composables/useGuideNav.js'
import PanelHeader from '@components/PanelHeader.vue'
import { getDiscussionsRequest } from '@services'
import { API_RESULT_LIMIT } from '@config'
import { Settings } from '@config/game.js'
import { discussionStore } from '@stores'
import { useI18n } from 'vue-i18n'
import {
  onMounted,
  readonly,
  computed,
  nextTick,
  provide,
  toRefs,
  watch,
  ref,
} from 'vue'

export default {
  components: {
    DiscussionForm,
    DiscussionCard,
    PanelHeader,
  },
  props: {
    type: {
      validator: (val) => DISCUSSION_TYPES.includes(val),
      required: true,
    },
    resource: {
      type: Object,
      required: true,
    },
    gameNameId: {
      type: String,
      required: true,
    },
    panelLoading: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update:count'],
  setup(props, { emit }) {
    const { panelLoading, type, gameNameId, resource } = toRefs(props)
    const resourceNameId = resource.value.name_id
    const editId = ref(null)
    let _offset = 0

    provide('editId', readonly(editId))
    provide('updateEditId', (id) => (editId.value = id))

    const { routeQuery } = useRoute()

    const { t } = useI18n()
    const {
      getComputedDiscussionsTotal,
      getComputedDiscussionPage,
      appendDiscussions,
      addDiscussions,
      getDiscussions,
    } = discussionStore(type.value)

    const discussionPage = getComputedDiscussionPage(
      gameNameId.value,
      resourceNameId
    )

    const moreCommentsToLoad = computed(
      () =>
        discussionPage.value.offset + discussionPage.value.count <
        discussionPage.value.total
    )

    const discussions = getDiscussions(gameNameId.value, resourceNameId)

    const discussionCount = getComputedDiscussionsTotal(
      gameNameId.value,
      resourceNameId
    )

    const canComment = computed(() => Settings.value.allowCommenting)

    const discussionText = computed(
      () => `${discussionCount.value} ${t('comment', discussionCount.value)}`
    )

    const isLoading = computed(
      () => panelLoading.value || discussionsLoading.value
    )

    const {
      data: discussionsData,
      run: runfetchDiscussions,
      error: discussionsError,
      loading: discussionsLoading,
    } = useAsync(
      () =>
        getDiscussionsRequest(gameNameId.value, resourceNameId, type.value, {
          _offset,
        }),
      t('comments_error_fetch')
    )

    const { status } = useStatus({
      loading: discussionsLoading,
      error: discussionsError,
    })

    watch(discussionCount, (_count) => emit('update:count', _count), {
      immediate: true,
    })

    onMounted(async () => {
      if (!discussions.value) await fetchDiscussions()
      if (routeQuery.value.comment) {
        // slight delay to ensure DOM is updated
        nextTick(() =>
          setTimeout(() => scrollToElement(`${routeQuery.value.comment}`), 100)
        )
      }
    })

    async function fetchDiscussions() {
      await runfetchDiscussions()
      if (!discussionsError.value) {
        addDiscussions(gameNameId.value, resourceNameId, discussionsData.value)
      }
    }

    async function loadMoreComments() {
      _offset += API_RESULT_LIMIT

      await runfetchDiscussions()

      if (!discussionsError.value) {
        appendDiscussions(
          gameNameId.value,
          resourceNameId,
          discussionsData.value
        )
      } else {
        addToast({
          title: t('comments_error_fetch'),
          isError: true,
          text: discussionsError.value,
        })
      }
    }

    return {
      DISCUSSION_TYPE_GUIDES,
      moreCommentsToLoad,
      loadMoreComments,
      discussionText,
      resourceNameId,
      DISCUSSION_ID,
      discussions,
      canComment,
      isLoading,
      isNumber,
      editId,
      status,
    }
  },
}
</script>
